/* eslint-disable no-shadow */
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import { useSnackbar } from 'notistack';
import { Redirect, useParams } from 'react-router-dom';
import Results from 'components/BiteDB/Components/Results';
import DBModuleHeaderLayout from 'components/Common/Layouts/DBModuleHeaderLayout';
import { useGetEntityById } from '../../api/bitedb/entities/entities.hooks';
import CircularProgress from '@mui/material/CircularProgress';
import { getDatabaseById } from 'api/bitedb/database/database.service';

type SupplierContentProps = {
  isRouteBlocked: boolean;
  supplierLoading: boolean;
  supplier: any;
  viewType: string;
};

/**
 * Module-scoped so React keeps a stable component identity across parent
 * re-renders. Defining this inside `Supplier` remounted `Results` whenever the
 * entity query refreshed (e.g. after adverse media), which closed open insight
 * panels (AI / ownership / media).
 */
const SupplierContent: React.FC<SupplierContentProps> = ({
  isRouteBlocked,
  supplierLoading,
  supplier,
  viewType,
}) => {
  if (isRouteBlocked) {
    return (
      <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <div className='hit-title'>
            You do not have access to this database for the Entity Hub. Switch to the correct team to view it.
          </div>
        </div>
    );
  }
  if (supplierLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress color='primary' />
      </div>
    );
  }
  if (!supplier) {
    return null;
  }
  return (
    <div>
      <Results supplier={supplier} viewType={viewType} />
    </div>
  );
};

const Supplier: React.FC = (props: any) => {
  const { id } = useParams<{ id: string }>();
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const selectedTeamId = cur_user?.selected_team?.id;
  const [viewType, setViewType] = useState<string>('');
  const [userHasMonitor, setUserHasMonitor] = useState(true);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [isRouteBlocked, setIsRouteBlocked] = useState(false);

  const {
    data: supplier,
    error: supplierError,
    isLoading: supplierLoading,
  } = useGetEntityById(id, userHasMonitor);

  useEffect(() => {
    if (supplierError) {
      enqueueSnackbar(`Error fetching entity: ${supplierError}`, {
        variant: 'error',
      });
    }
  }, [supplierError, enqueueSnackbar]);

  const requiredTiers = ['business', 'enterprise'];

  const checkSubscription = async () => {
    if (cur_user.quickcodeOnly) {
      setIsCheckingSubscription(false);
      return;
    }
    try {
      if (
        requiredTiers.some((role) =>
          cur_user.selected_team?.attributes?.tier.includes(role),
        )
      ) {
        return;
      }
      if (requiredTiers.includes(cur_user.license)) {
        return;
      }
      setUserHasMonitor(false);
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  useEffect(() => {
    if (cur_user.selected_team) {
      checkSubscription();
    }
  }, [cur_user.selected_team]);

  useEffect(() => {
    const verifyDatabaseOwnership = async () => {
      if (!userHasMonitor || !supplier?.database_id || !selectedTeamId) {
        return;
      }
      const database = await getDatabaseById(supplier.database_id);
      setIsRouteBlocked(
        !!database?.group_id && database.group_id !== selectedTeamId,
      );
    };

    verifyDatabaseOwnership();
  }, [
    supplier?.database_id,
    selectedTeamId,
    userHasMonitor,
  ]);

  useEffect(() => {
    if (props.location.state) {
      const { objectData } = props.location.state;
      if (objectData && objectData.viewType) {
        setViewType(objectData.viewType);
      }
    }
  }, [supplier, props.location.state]);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  // The "Back to X" affordance now lives in the breadcrumb instead of a
  // standalone button — this mirrors whatever GoTo() used to jump to based
  // on where the entity was opened from, so that context isn't lost.
  // Database/Review Queue/Stats Roll Up all read their own state (status
  // filter, sort, page) from their URL's query string; the row link that
  // brought us here already forwards that same query string onto this
  // page's URL (see ReviewQRow's `currentSearch`), so re-attaching
  // props.location.search to the crumb's target restores it exactly,
  // instead of resetting that page to its defaults.
  const parentSearch = props.location.search || '';
  const middleCrumb =
    viewType === 'Database' && supplier?.database_id
      ? {
          label: supplier?.database_name || 'Database',
          to: {
            pathname: `/database/${supplier.database_id}`,
            search: parentSearch,
          },
        }
      : viewType === 'Review Queue'
      ? {
          label: 'Review Queue',
          to: { pathname: '/review-queue', search: parentSearch },
        }
      : viewType === 'Stats Roll Up'
      ? {
          label: 'Stats Roll Up',
          to: { pathname: '/stats-roll-up', search: parentSearch },
        }
      : undefined;

  const content = (
    <SupplierContent
      isRouteBlocked={isRouteBlocked}
      supplierLoading={supplierLoading}
      supplier={supplier}
      viewType={viewType}
    />
  );

  return (
    <>
      {isWhiteLabel ? (
        <div
          style={{
            margin: '0px 50px',
          }}
        >
          {content}
        </div>
      ) : (
        <DBModuleHeaderLayout
          title={supplier?.name || 'Loading...'}
          hubLabel='BITE DB'
          hubTo='/bite-db/entities'
          middleCrumb={middleCrumb}
          hideTitleRow
        >
          {content}
        </DBModuleHeaderLayout>
      )}
    </>
  );
};

export default Supplier;
